import {Component, inject, OnDestroy, OnInit, Renderer2} from "@angular/core";
import {Store} from "@ngrx/store";
import {FormBuilder, FormControl, Validators} from "@angular/forms";
import {map, Subscription} from "rxjs";
import {DecimalFormatPipe} from "../../../shared/pipes/decimal-format/decimal-format.pipe";
import {numberNotBiggerThan190000m} from "../../../shared/utils/form-validators";
import {GeneralPartnerValuationActions} from "../../store/asset.actions";
import {EMPTY_FUND_INVESTMENT, FundInvestment} from "../../../fund/models/fund-investement";
import {
    selectBaselineGeneralPartnerValuation,
    selectGeneralPartnerCapexTableData,
    selectGeneralPartnerValuation,
    selectGeneralPartnerValuationTableData,
    selectGeneralPartnerValuationView,
    selectIsAnyAssetFormInEditMode,
    selectRelatedFundCurrencyIsoCode
} from "../../store/asset/asset.selectors";
import {TraceableMoney} from "../../../shared/model/traceable";
import {CodeTableEnum} from "../../../shared/model/code";

@Component({
    selector: "valumize-asset-gp-valuation",
    templateUrl: "./asset-gp-valuation.component.html",
    styleUrls: ["./asset-gp-valuation.component.scss"],
    standalone: false
})
export class AssetGpValuationComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly renderer = inject(Renderer2);

    subscriptions: Subscription[] = [];

    generalPartnerValuation$ = this.store.select(selectGeneralPartnerValuation);
    fundCurrencyIso$ = this.store.select(selectRelatedFundCurrencyIsoCode);
    generalPartnerValuationTableData$ = this.store.select(selectGeneralPartnerValuationTableData);
    generalPartnerCapexTableData$ = this.store.select(selectGeneralPartnerCapexTableData);
    persistedFundInvestment: FundInvestment = EMPTY_FUND_INVESTMENT;
    baselineFundInvestment: FundInvestment = EMPTY_FUND_INVESTMENT;
    isDataset = false;

    isEditDisabled$ = this.store.select(selectIsAnyAssetFormInEditMode);
    isEditable = false;

    codeTableGpIndication = CodeTableEnum.ASSET_GPINDICATION;

    fundId: number | undefined;
    fundReportId: number | undefined;
    displayedColumns: string[] = ["name", "fundLevel"];

    gpValuationForm = this.formBuilder.group({
        gpIndication: this.formBuilder.control<string>("", {nonNullable: true}),
        remainingCost: this.formBuilder.control<number | null>(null, {validators: [Validators.required, numberNotBiggerThan190000m()]}),
        mezzanine: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        preferredEquity: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        commonEquity: this.formBuilder.control<number | null>(null, {validators: [Validators.required, numberNotBiggerThan190000m()]}),
        realizedCost: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        realizedGains: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        committedInvestment: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]})
    });

    nextFocus: string | undefined;
    controlNamesForFocus = ["remainingCost", "mezzanine", "preferredEquity", "commonEquity", "realizedCost", "realizedGains", "committedInvestment"];

    constructor() {
    }

    ngOnInit() {
        this.subscriptions.push(this.generalPartnerValuation$.pipe(map((fundInvestment) => {
            this.persistedFundInvestment = fundInvestment.data;
        })).subscribe());

        this.subscriptions.push(
            this.store.select(selectBaselineGeneralPartnerValuation).pipe(map((baselineFundInvestment) => {
                if (baselineFundInvestment.data.id) {
                    this.isDataset = true;
                    this.baselineFundInvestment = baselineFundInvestment.data;
                } else {
                    this.isDataset = false;
                }
            })).subscribe());

        this.subscriptions.push(this.store.select(selectGeneralPartnerValuationView).pipe(map((gpValuation) => {
            this.isEditable = gpValuation.isEditable;
            const gpValuationEntry = gpValuation.gpValuation.data;
            if (gpValuationEntry) {
                this.gpValuationForm.patchValue({
                    gpIndication: gpValuationEntry.gpIndication?.code ?? "",
                    remainingCost: DecimalFormatPipe.transformFromMillionsNum(gpValuationEntry.remainingCost?.amount ?? undefined),
                    mezzanine: DecimalFormatPipe.transformFromMillionsNum(gpValuationEntry.mezzanine?.amount ?? undefined),
                    preferredEquity: DecimalFormatPipe.transformFromMillionsNum(gpValuationEntry.preferredEquity?.amount ?? undefined),
                    commonEquity: DecimalFormatPipe.transformFromMillionsNum(gpValuationEntry.commonEquity?.amount ?? undefined),
                    realizedCost: DecimalFormatPipe.transformFromMillionsNum(gpValuationEntry.realizedCost?.amount ?? undefined),
                    realizedGains: DecimalFormatPipe.transformFromMillionsNum(gpValuationEntry.realizedGains?.amount ?? undefined),
                    committedInvestment: DecimalFormatPipe.transformFromMillionsNum(gpValuationEntry.committedInvestment?.amount ?? undefined)
                });
            }
            if (this.isEditable) {
                this.gpValuationForm.enable();
                // Use a setTimeout to refocus on the next Javascript event loop tick,
                // giving Angular a chance to update the DOM first
                setTimeout(() => {
                    if (this.nextFocus) {
                        this.renderer.selectRootElement("#" + this.nextFocus).focus();
                    }
                }, 0);
            } else {
                this.gpValuationForm.disable();
            }
        })).subscribe());
    }

    calc = (focus: string) => {
        const currentIndex = this.controlNamesForFocus.findIndex(control => control === focus);
        this.nextFocus = this.controlNamesForFocus[currentIndex + 1] || this.controlNamesForFocus[0];
        if (this.gpValuationForm.valid) {
            const form = this.gpValuationForm.getRawValue();
            this.store.dispatch(GeneralPartnerValuationActions.calc({
                gpIndication: form.gpIndication || undefined,
                remainingCost: DecimalFormatPipe.transformToMillionsNum(form.remainingCost),
                mezzanine: DecimalFormatPipe.transformToMillionsNum(form.mezzanine),
                preferredEquity: DecimalFormatPipe.transformToMillionsNum(form.preferredEquity),
                commonEquity: DecimalFormatPipe.transformToMillionsNum(form.commonEquity),
                realizedCost: DecimalFormatPipe.transformToMillionsNum(form.realizedCost),
                realizedGains: DecimalFormatPipe.transformToMillionsNum(form.realizedGains),
                committedInvestment: DecimalFormatPipe.transformToMillionsNum(form.committedInvestment)
            }));
        }
    };

    save() {
        if (this.gpValuationForm.valid) {
            const form = this.gpValuationForm.getRawValue();
            const fundInvestmentToSafe: FundInvestment = {
                ...this.persistedFundInvestment,
                gpIndication: {
                    ...this.persistedFundInvestment?.gpIndication,
                    code: form.gpIndication
                },
                remainingCost: {
                    ...this.persistedFundInvestment?.remainingCost,
                    amount: DecimalFormatPipe.transformToMillionsNum(form.remainingCost)
                },
                mezzanine: {
                    ...this.persistedFundInvestment?.mezzanine,
                    amount: DecimalFormatPipe.transformToMillionsNum(form.mezzanine)
                },
                preferredEquity: {
                    ...this.persistedFundInvestment?.preferredEquity,
                    amount: DecimalFormatPipe.transformToMillionsNum(form.preferredEquity)
                },
                commonEquity: {
                    ...this.persistedFundInvestment?.commonEquity,
                    amount: DecimalFormatPipe.transformToMillionsNum(form.commonEquity)
                },
                realizedCost: {
                    ...this.persistedFundInvestment?.realizedCost,
                    amount: DecimalFormatPipe.transformToMillionsNum(form.realizedCost)
                },
                realizedGains: {
                    ...this.persistedFundInvestment?.realizedGains,
                    amount: DecimalFormatPipe.transformToMillionsNum(form.realizedGains)
                },
                committedInvestment: {
                    ...this.persistedFundInvestment?.committedInvestment,
                    amount: DecimalFormatPipe.transformToMillionsNum(form.committedInvestment)
                }
            };
            this.store.dispatch(GeneralPartnerValuationActions.save({fundInvestment: fundInvestmentToSafe ?? EMPTY_FUND_INVESTMENT}));
        }
    }

    editMode = () => this.store.dispatch(GeneralPartnerValuationActions.edit());

    cancel = () => this.store.dispatch(GeneralPartnerValuationActions.cancel());

    formControlIsEditable(type: string): boolean {
        return (
            type === "remainingCost" ||
            type === "mezzanine" ||
            type === "preferredEquity" ||
            type === "commonEquity" ||
            type === "realizedCost" ||
            type === "realizedGains" ||
            type === "committedInvestment"
        );
    }

    styleRequiredFormFieldWithAsterisk(type: string): string {
        return type === "remainingCost" || type === "commonEquity" ? "required-asterisk" : "";
    }

    styleRowImportant(row: any): string {
        const importantRowTypes = ["nav"];
        return (importantRowTypes.includes(row.type)) ? "row-important" : "";
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    overwriteFundLevelValue(fieldKey: string, baseline: TraceableMoney): void {
        const ctrl = this.getControl(fieldKey);
        const num = this.toNumberForInput(baseline);

        if (num != null) {
            ctrl.setValue(num);
            // preserve your focus flow and downstream calcs
            this.calc(fieldKey);
        }
    }

    getBaselineChipColor(control: FormControl, baseline: TraceableMoney, currentFundLevel: TraceableMoney): string {
        const baselineNum = this.toNumberForInput(baseline);
        const currentNum = this.toNumberForInput(currentFundLevel);
        const controlNum = Number(control.value);

        if (baselineNum == null) {
            return "chip-diff-value";
        }

        if (controlNum === baselineNum && baselineNum !== currentNum) {
            return "chip-overwritten-value";
        } else if (baselineNum === currentNum) {
            return "chip-same-value";
        } else {
            return "chip-diff-value";
        }
    }

    getBaselineChipColorReadonly(baseline: TraceableMoney, currentFundLevel: TraceableMoney): string {
        const baselineNum = this.toNumberForInput(baseline);
        const currentNum = this.toNumberForInput(currentFundLevel);

        if (baselineNum == null) {
            return "chip-diff-value";
        }
        return baselineNum === currentNum ? "chip-same-value" : "chip-diff-value";
    }

    private toNumberForInput(value?: TraceableMoney): number | undefined {
        if (!value?.amount) {
            return;
        }
        return DecimalFormatPipe.transformFromMillionsNum(value.amount);
    }

    getControl(fieldKey: string): FormControl {
        const ctrl = this.gpValuationForm?.get(fieldKey);
        if (!ctrl) {
            throw new Error(`FormControl not found for ${fieldKey}`);
        }
        return ctrl as FormControl;
    }
}
